<template>
  <div>
    <span v-if="rangeKeyMask">
      <input
        class="date-range-picker__input-range-text"
        type="text"
        :value="rangeKeyMask"
        readonly
      >
    </span>
    <span
      v-else
      class="date-range-picker__input"
    >
      <DateInput
        ref="startInput"
        v-model="innerStart"
        :disabled="disabled"
        @keydown.native.enter="goToEndInput"
        @keydown.native.tab="goToEndInput"
        @keyup.native.right="handleBtnRightClick"
        @blur="handleBlurStartDate"
      />
      <span class="date-range-picker__separator">—</span>
      <DateInput
        ref="endInput"
        v-model="innerEnd"
        :disabled="disabled"
        @keydown.native.enter="submitDate"
        @keyup.native.left="handleBtnLeftClick"
        @blur="handleBlurEndDate"
      />
    </span>
    <MIcon
      :icon="calendarIcon"
      class="date-range-picker__calendar-icon"
      @click="handleIconClick"
    />
  </div>
</template>

<script>
  import DateInput from 'components/Inputs/Dates/common/DateInput.vue';
  import MIcon from 'components/Design/MIcon.vue';
  import calendarIcon from 'assets/icons/calendar.svg';

  import moment from 'moment';

  export default {
    name: 'DateRangeInput',
    components: {DateInput, MIcon},
    props: {
      range: {
        type: Object,
        default: Object
      },
      maxDate: {
        type: Date,
        default: null
      },
      minDate: {
        type: Date,
        default: null
      },
      disabled: {
        type: Boolean,
        default: false
      },
      rangeKeyMask: {
        type: String,
        default: null
      },
      small: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        calendarIcon,
        innerStart: null,
        innerEnd: null
      };
    },
    watch: {
      range: {
        handler () {
          this.updateInnerDates();
        },
        immediate: true
      }
    },
    mounted () {
      this.updateInnerDates();
    },
    methods: {
      handleIconClick () {
        if (this.$refs.startInput) {
          this.$refs.startInput.$el.focus();
        }
      },
      getCaretPosInEl (el) {
        if (el.selectionStart || el.selectionStart === 0) {
          return el.selectionStart;
        }
        return 0;
      },
      setCaretPosInEl (el, posIndex) {
        el.focus();
        el.setSelectionRange(posIndex, posIndex);
      },
      handleBlurStartDate () {
        this.validateEndDate();
        this.submitChanges();
      },
      handleBlurEndDate () {
        this.validateStartDate();
        this.submitChanges();
      },
      handleBtnRightClick () {
        const fieldEl = this.$refs.startInput.$el;
        const pos = this.getCaretPosInEl(fieldEl);
        if (pos === this.$refs.startInput.$el.value.length) {
          this.setCaretPosInEl(this.$refs.endInput.$el, 0);
        }
      },
      handleBtnLeftClick () {
        const fieldEl = this.$refs.endInput.$el;
        const pos = this.getCaretPosInEl(fieldEl);
        if (pos === 0) {
          this.setCaretPosInEl(this.$refs.startInput.$el, this.$refs.startInput.$el.value.length);
        }
      },
      fitDateInRange (date) {
        if (this.minDate && date < this.minDate) {
          return this.minDate;
        }
        if (this.maxDate && date > this.maxDate) {
          return this.maxDate;
        }
        return date;
      },
      updateInnerDates () {
        this.innerStart = this.range.startDate;
        this.innerEnd = this.range.endDate;
      },
      goToEndInput () {
        this.validateEndDate();
        this.submitChanges();
        this._$nextFrame(() => {
          this.$refs.endInput.$el.focus();
        });
      },
      submitDate () {
        this.validateEndDate();
        this.validateStartDate();
        this.submitChanges();
        this.$emit('dateSubmitted');

        // defocus input after date confirmation (OK button click or ENTER press)
        this._$nextFrame(() => {
          this.$refs.startInput.$el.blur();
          this.$refs.endInput.$el.blur();
        });
      },
      validateEndDate () {
        if (!moment(this.innerEnd).isValid()) {
          return;
        }

        this.innerEnd = this.fitDateInRange(this.innerEnd);

        if (this.innerStart > this.innerEnd) {
          this.innerEnd = this.innerStart;
        }
      },
      validateStartDate () {
        if (!moment(this.innerStart).isValid()) {
          return;
        }

        this.innerStart = this.fitDateInRange(this.innerStart);

        if (this.innerStart > this.innerEnd) {
          this.innerEnd = this.innerStart;
        }
      },
      submitChanges () {
        this.$emit(
          'input',
          {
            startDate: this.innerStart,
            endDate: this.innerEnd
          }
        );
      }
    }
  };
</script>

<style>
  .date-range-picker__input {
    display: flex;
    align-items: center;
  }

  .date-range-picker__text input,
  .date-range-picker__text input[type='text'].date-range-picker__input-range-text {
    font-size: var(--main-font-size);

    color: var(--secondary-text-color);

    text-align: center;

    width: 60px;

    padding: 5px;

    margin-bottom: 0;

    border: none;

    border-radius: 6px;
  }

  /* for input with range key text */
  .date-range-picker__text input[type='text'].date-range-picker__input-range-text {
    text-align: left;

    width: 140px;

    padding: 2px 0 2px 5px;
  }

  .date-range-picker__text .date-range-picker__placeholder input {
    width: 122px;
  }

  .date-range-picker__calendar-icon {
    color: var(--secondary-text-color);

    flex-shrink: 0;

    width: 13px;
    height: 13px;

    margin-left: 10px;
    margin-bottom: 2px;
    margin-right: 4px;
  }

  .date-range-picker__text input:focus {
    border: none;
    outline: none;
  }
</style>
